import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Globe, FileText, CheckCircle, Users, Calendar, DollarSign, Clock, Award, Star, Play, ArrowRight, Phone, Mail, MapPin } from 'lucide-react';
import ContactUsForm from '../components/ContactForm';
import GoogleReviews from '../components/GoogleReviews';
import { Link } from "react-router-dom";


const handleScrollToContact = () => {
  const form = document.getElementById("contact-form");
  if (form) {
    form.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
};

const countries = [
    {
  name: "Australia",
  flag: "🇦🇺",
  supportedVisas: [
  "Student Visa",
  "Visitor Visa",
  "Work Visa",
  "Dependent Visa"
],
  image: "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?q=80&w=3030&auto=format&fit=crop",
  details: [
    { label: "Visa Types", value: "Student (Subclass 500), Visitor, Work, PR" },
    { label: "Processing Time", value: "4–12 weeks" },
    { label: "Financial Requirement", value: "AU$24,505/year" },
    { label: "Work Rights", value: "24 hrs/week (students)" },
    { label: "Post-Study Options", value: "2–4 year PSW" },
    { label: "Application Fee", value: "AU$710" }
  ]
},

    {
  name: "Canada",
  flag: "🇨🇦",
  supportedVisas: ["Student Visa", "Visitor Visa", "Work Visa", "Dependent Visa"],
  image: "https://images.unsplash.com/photo-1530025809667-1f4bcff8e60f?q=80&w=2891&auto=format&fit=crop",
  details: [
    { label: "Visa Types", value: "Student, Visitor, Work, PR" },
    { label: "Processing Time", value: "4–6 weeks" },
    { label: "Financial Requirement", value: "Proof of funds required" },
    { label: "Work Rights", value: "20 hrs/week (students)" },
    { label: "Post-Study Options", value: "PGWP available" },
    { label: "Health Insurance", value: "Mandatory" }
  ]
},
    {
  name: "UK",
  flag: "🇬🇧",
  supportedVisas: ["Student Visa", "Visitor Visa", "Work Visa", "Dependent Visa"],
  image: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=2070&auto=format&fit=crop",
  details: [
    { label: "Visa Types", value: "Student, Visitor, Skilled Worker" },
    { label: "Processing Time", value: "3–8 weeks" },
    { label: "Financial Requirement", value: "£1,334/month (London)" },
    { label: "Work Rights", value: "20 hrs/week (students)" },
    { label: "Post-Study Options", value: "2-year Graduate Route" },
    { label: "Application Fee", value: "£490" }
  ]
},
    {
  name: "USA",
  flag: "🇺🇸",
  supportedVisas: ["Student Visa", "Visitor Visa", "Work Visa", "Dependent Visa"],
  image: "https://images.unsplash.com/photo-1485738422979-f5c462d49f74?q=80&w=2099&auto=format&fit=crop",
  details: [
    { label: "Visa Types", value: "F1, B1/B2, H1B" },
    { label: "Processing Time", value: "3–5 weeks" },
    { label: "Financial Requirement", value: "Tuition + living expenses" },
    { label: "Work Rights", value: "On-campus only" },
    { label: "Post-Study Options", value: "OPT / H1B" },
    { label: "Visa Validity", value: "Course duration + grace period" }
  ]
}

  ];



const visaDetails = [
  {
    type: "Student Visa",
    requirements: [
      "Offer letter from recognized institution",
      "Proof of sufficient funds",
      "English language proficiency",
      "Medical & police clearance",
      "Genuine Student / SOP"
    ],
    eligibility: [
      "Minimum academic qualification",
      "Clear study intent",
      "Financial stability",
      "No major visa refusal history"
    ],
    process: [
      "Profile Evaluation",
      "University / Course Finalization",
      "Document Preparation",
      "Visa Filing",
      "Biometrics / Interview",
      "Visa Decision"
    ],
    documents: [
      "Passport",
      "Offer Letter",
      "Academic Transcripts",
      "Bank Statements",
      "English Test Scores",
      "SOP"
    ]
  },
  {
    type: "Visitor Visa",
    requirements: [
      "Valid passport",
      "Purpose of visit",
      "Financial proof",
      "Travel itinerary",
      "Strong home-country ties"
    ],
    eligibility: [
      "Temporary travel intent",
      "Stable income or sponsor",
      "Clean travel history"
    ],
    process: [
      "Eligibility Assessment",
      "Document Collection",
      "Application Submission",
      "Biometrics",
      "Visa Decision"
    ],
    documents: [
      "Passport",
      "Bank Statements",
      "Invitation Letter (if any)",
      "Travel Plan",
      "Employment Proof"
    ]
  }
];


const VisaPage = () => {
  
  const visaRefs = useRef({});
const [highlightedVisa, setHighlightedVisa] = useState(null);
const [selectedVisaType, setSelectedVisaType] = useState("All");

  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredVisa, setHoveredVisa] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
const filteredCountries =
  selectedVisaType === "All"
    ? countries
    : countries.filter((country) =>
        country.supportedVisas.includes(selectedVisaType)
      );
      useEffect(() => {
  setCurrentIndex(0);
}, [selectedVisaType]);

const handleScrollToVisa = (visaType) => {
  const el = visaRefs.current[visaType];

  if (!el) {
    handleScrollToContact(); // Work / Dependent fallback
    return;
  }

  el.scrollIntoView({ behavior: "smooth", block: "start" });
  setHighlightedVisa(visaType);

  setTimeout(() => {
    setHighlightedVisa(null);
  }, 3000);
};


  const visaTypes = [
    { 
      name: "Student Visa", 
      icon: "🎓",
      description: "For international education and academic pursuits",
      features: ["University", "College", "Language Courses", "Research Programs"]
    },
    { 
      name: "Visitor Visa", 
      icon: "✈️",
      description: "For tourism, business meetings, and short-term visits",
      features: ["Tourism", "Family Visit", "Business Meetings", "Medical Treatment"]
    },
    { 
      name: "Work Visa", 
      icon: "💼",
      description: "For professional employment opportunities abroad",
      features: ["Skilled Worker", "Temporary Work", "Seasonal Work", "Intra-company Transfer"]
    },
    { 
      name: "Dependent Visa", 
      icon: "👨‍👩‍👧‍👦",
      description: "For family members of primary visa holders",
      features: ["Spouse", "Children", "Parents", "Extended Family"]
    }
  ];

  

  const visaProcess = [
    {
      step: 1,
      title: "Initial Consultation",
      description: "Discuss your visa requirements and eligibility with our expert consultants",
      icon: <Users className="w-8 h-8" />,
      details: "Our experienced consultants will assess your profile and recommend the best visa options for your goals."
    },
    {
      step: 2,
      title: "Document Preparation",
      description: "Compile and organize all necessary documents for your visa application",
      icon: <FileText className="w-8 h-8" />,
      details: "We'll provide you with a comprehensive checklist and help you prepare all required documents."
    },
    {
      step: 3,
      title: "Application Submission",
      description: "Submit your visa application with complete documentation",
      icon: <CheckCircle className="w-8 h-8" />,
      details: "Our team will review your application thoroughly before submission to ensure highest success rate."
    },
    {
      step: 4,
      title: "Interview Preparation",
      description: "Get ready for your visa interview with mock sessions and expert guidance",
      icon: <Award className="w-8 h-8" />,
      details: "Comprehensive interview preparation including mock sessions and expert tips for success."
    }
  ];

  const successStories = [
    {
      name: "Sarah Johnson",
      country: "Australia",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b789?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      story: "The guidance provided by M.S. Education Consultant was invaluable. I was able to obtain my visa smoothly and am now ready to pursue my studies abroad.",
      rating: 5,
      visa: "Student Visa"
    },
    {
      name: "Michael Chen",
      country: "Canada",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      story: "Excellent service and professional approach. The team helped me navigate the complex visa process with ease and confidence.",
      rating: 5,
      visa: "Work Visa"
    },
    {
      name: "Emily Rodriguez",
      country: "UK",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      story: "Professional, reliable, and results-oriented. I couldn't have asked for better support during my visa application process.",
      rating: 5,
      visa: "Student Visa"
    }
  ];

  const faqs = [
    {
      question: "What are the general requirements for a student visa?",
      answer: "General requirements include acceptance letter from institution, proof of funds, academic transcripts, English proficiency test scores, and health insurance."
    },
    {
      question: "How long does the visa application process take?",
      answer: "Processing times vary by country and visa type, typically ranging from 2-12 weeks. We provide estimated timelines for each destination."
    },
    {
      question: "What documents are needed for a student visa?",
      answer: "Required documents include passport, acceptance letter, financial statements, academic records, English test scores, and medical certificates."
    },
    {
      question: "Can I apply for a visa on my own or do I need an agent?",
      answer: "While you can apply independently, our expert guidance significantly increases success rates and reduces processing complications."
    }
  ];


  const handlePrevious = () => {
    setCurrentIndex(prev => prev === 0 ? filteredCountries.length - 1 : prev - 1);
  };

  const handleNext = () => {
    setCurrentIndex(prev => prev === filteredCountries.length - 1 ? 0 : prev + 1);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % visaProcess.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

const currentCountry = filteredCountries[currentIndex];

if (!filteredCountries.length) {
  return (
    <section className="py-20 bg-white text-center">
      <h3 className="text-2xl font-bold text-gray-900 mb-3">
        No destinations available
      </h3>
      <p className="text-gray-600 max-w-xl mx-auto">
        We currently do not offer country options for this visa type.
        Please book a consultation for personalized guidance.
      </p>

      <button
        onClick={handleScrollToContact}
        className="mt-6 px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition"
      >
        Book Free Consultation
      </button>
    </section>
  );
}


  return (
    <div className="min-h-screen bg-white">
      
{/* Hero Section */}
<div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
    <div className="grid lg:grid-cols-2 gap-12 items-center">

      {/* LEFT CONTENT */}
      <div className="space-y-8">
        <div className="space-y-4">
          <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
            Your Gateway to
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400">
              Global Opportunities
            </span>
          </h1>

          <p className="text-xl text-blue-100 leading-relaxed max-w-2xl">
            Expert visa guidance to help you achieve your international education and career goals with confidence and success.
          </p>
        </div>

        {/* CTA BUTTONS */}
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handleScrollToContact}
            className="px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl shadow-lg transition"
          >
            Book Free Consultation
          </button>

          <a
            href="tel:+919510142218"
            className="px-8 py-4 bg-white text-blue-700 font-semibold rounded-xl shadow-lg hover:bg-blue-50 transition flex items-center gap-2"
          >
            <Phone className="w-5 h-5" />
            Call Now
          </a>
        </div>

        {/* TRUST SIGNALS */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-white/20">
          <div>
            <p className="text-2xl font-bold">15+</p>
            <p className="text-sm text-blue-100">Years Experience</p>
          </div>
          <div>
            <p className="text-2xl font-bold">10,000+</p>
            <p className="text-sm text-blue-100">Visas Processed</p>
          </div>
          <div>
            <p className="text-2xl font-bold">99%</p>
            <p className="text-sm text-blue-100">Success Rate</p>
          </div>
          <div>
            <p className="text-2xl font-bold">7+</p>
            <p className="text-sm text-blue-100">Countries Served</p>
          </div>
        </div>
      </div>

      {/* RIGHT CARD */}
      <div className="relative">
        <div className="bg-white text-gray-800 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl mb-6 mx-auto">
            <Globe className="w-10 h-10 text-white" />
          </div>

          <div className="text-center">
            <h3 className="text-2xl font-bold mb-2">
              Free Visa Assessment
            </h3>
            <p className="text-gray-600 mb-6">
              30-minute expert session to check eligibility, documents, and visa pathway
            </p>
<Link to="/eligibility">
            <button
              onClick={handleScrollToContact}
              className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition"
            >
              Check My Eligibility
            </button>
</Link>
            <p className="text-xs text-gray-500 mt-3">
              No obligation • 100% confidential
            </p>
          </div>
        </div>
      </div>

    </div>
  </div>
</div>



      {/* Visa Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Visa Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our streamlined process ensures a smooth and successful visa application journey
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {visaProcess.map((process, index) => (
              <div key={index} className="relative">
                <div className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 ${activeStep === index ? 'ring-4 ring-blue-500 ring-opacity-50' : ''}`}>
                  <div className={`w-16 h-16 rounded-xl mb-6 flex items-center justify-center ${
                    activeStep === index ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                  } transition-all duration-300`}>
                    {process.icon}
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <span className="bg-orange-500 text-white text-sm font-bold px-2 py-1 rounded-full mr-2">
                        {process.step}
                      </span>
                      <h3 className="text-lg font-bold text-gray-900">{process.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{process.description}</p>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-xs text-gray-500">{process.details}</p>
                  </div>
                </div>
                
                {index < visaProcess.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-blue-500 to-orange-500 transform -translate-y-1/2"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Country Carousel Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            {selectedVisaType !== "All" && (
  <p className="text-sm text-blue-600 font-semibold mb-2">
    Showing destinations for: {selectedVisaType}
  </p>
)}

            <h2 className="text-4xl font-bold text-gray-900 mb-4">Destination Highlights</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore visa requirements and opportunities for your dream destination
            </p>
          </div>
          
          <div className="relative">
            <button
              onClick={handlePrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center text-blue-600 hover:text-blue-800 transition-all duration-300"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="grid lg:grid-cols-2">
                <div className="relative h-80 lg:h-auto">
                  <img
                    src={currentCountry.image}
                    alt={currentCountry.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                  <div className="absolute bottom-6 left-6">
                    <div className="flex items-center space-x-3">
                      <span className="text-4xl">{currentCountry.flag}</span>
                      <h3 className="text-3xl font-bold text-white">{currentCountry.name}</h3>
                    </div>
                  </div>
                </div>
                
                <div className="p-8 lg:p-12">
                  <h3 className="text-3xl font-bold text-gray-900 mb-8">Visa Information</h3>
                  <div className="space-y-4">
                    {currentCountry.details.map((detail, index) => (
                      <div key={index} className="flex justify-between items-start py-3 border-b border-gray-100 hover:bg-gray-50 px-4 rounded-lg transition-colors duration-200">
                        <span className="font-semibold text-blue-700 text-sm">{detail.label}:</span>
                        <span className="text-gray-700 text-sm text-right ml-4">{detail.value}</span>
                      </div>
                    ))}
                  </div>
                  
                  
                </div>
              </div>
            </div>
            
            <button
              onClick={handleNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center text-blue-600 hover:text-blue-800 transition-all duration-300"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex justify-center mt-8 space-x-2">
            {filteredCountries.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Visa Types Section */}
<section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

    {/* Section Header */}
    <div className="text-center mb-16">
      <h2 className="text-4xl font-bold text-gray-900 mb-4">
        Types of Visa
      </h2>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
        Select a visa category to view requirements or speak with an expert
      </p>
    </div>

    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
      {visaTypes.map((visa, index) => {
        const requiresConsultation = ["Work Visa", "Dependent Visa"].includes(
          visa.name
        );

        return (
          <div
            key={index}
            onClick={() => {
  setSelectedVisaType(visa.name);
  handleScrollToVisa(visa.name);
}}

            className={`relative group cursor-pointer ${
  selectedVisaType === visa.name
    ? "ring-2 ring-blue-600 scale-[1.02]"
    : ""
}`}

            onMouseEnter={() => setHoveredVisa(index)}
            onMouseLeave={() => setHoveredVisa(null)}
          >
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-3 group-hover:bg-gradient-to-br group-hover:from-blue-50 group-hover:to-white relative">

              {/* BADGE */}
              {requiresConsultation && (
                <span className="absolute top-4 right-4 text-xs font-semibold px-3 py-1 rounded-full bg-orange-100 text-orange-700">
                  Consultation Required
                </span>
              )}

              <div className="text-center">
                <div className="text-6xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  {visa.icon}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {visa.name}
                </h3>

                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  {visa.description}
                </p>

                <div className="space-y-2">
                  {visa.features.map((feature, featureIndex) => (
                    <div
                      key={featureIndex}
                      className="flex items-center justify-center text-sm text-gray-500"
                    >
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>

                <div className="mt-6 text-blue-600 font-semibold text-sm group-hover:underline">
                  {requiresConsultation ? "Consult Expert →" : "View Details →"}
                </div>
              </div>

              {/* Hover underline */}
              <div
                className={`absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-blue-500 to-orange-500 rounded-b-2xl transform origin-center transition-all duration-300 ${
                  hoveredVisa === index ? "scale-x-100" : "scale-x-0"
                }`}
              ></div>
            </div>
          </div>
        );
      })}
    </div>

  </div>
</section>



      {/* Detailed Visa Information Section */}
<section className="py-20 bg-gray-50">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

    {/* Section Header */}
    <div className="text-center mb-16">
      <h2 className="text-4xl font-bold text-gray-900 mb-4">
        Visa Information
      </h2>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
        Clear eligibility, documentation, and step-by-step guidance for each visa category
      </p>
    </div>

    {visaDetails.map((visa, index) => (
      <div
  key={index}
  ref={(el) => (visaRefs.current[visa.type] = el)}
  className={`mb-16 bg-white rounded-3xl p-8 lg:p-12 shadow-lg scroll-mt-32 transition-all duration-500
    ${
      highlightedVisa === visa.type
        ? "ring-4 ring-blue-500 ring-opacity-40 bg-blue-50/40"
        : ""
    }
  `}
>

        {/* Visa Title */}
        <h3 className="text-3xl font-bold text-blue-700 mb-8">
          {visa.type}
        </h3>

        <div className="grid lg:grid-cols-2 gap-10">

          {/* LEFT COLUMN */}
          <div className="space-y-8">
            <div>
              <h4 className="text-xl font-semibold mb-3">Requirements</h4>
              <ul className="space-y-2">
                {visa.requirements.map((item, i) => (
                  <li key={i} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xl font-semibold mb-3">Eligibility</h4>
              <ul className="space-y-2">
                {visa.eligibility.map((item, i) => (
                  <li key={i} className="flex items-start">
                    <ArrowRight className="w-5 h-5 text-blue-600 mr-2 mt-1" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-8">
            <div>
              <h4 className="text-xl font-semibold mb-3">
                Step-by-Step Process
              </h4>
              <ol className="list-decimal list-inside space-y-2">
                {visa.process.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>

            <div>
              <h4 className="text-xl font-semibold mb-3">
                Document Checklist
              </h4>
              <ul className="space-y-2">
                {visa.documents.map((doc, i) => (
                  <li key={i} className="flex items-start">
                    <FileText className="w-5 h-5 text-gray-600 mr-2 mt-1" />
                    <span>{doc}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10">
          <button
            onClick={handleScrollToContact}
            className="px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition"
          >
            Book Free Consultation
          </button>
          <p className="text-xs text-gray-500 mt-3">
            Talk to a certified visa expert • No obligation
          </p>
        </div>
      </div>
    ))}
  </div>
</section>




     {/* Step-by-Step Visa Process Section */}
<section className="py-16 lg:py-20 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

    {/* Section Header */}
    <div className="text-center mb-14 lg:mb-16">
      <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
        Our Step-by-Step Visa Process
      </h2>
      <p className="text-lg text-gray-600 max-w-3xl mx-auto">
        A structured, transparent process designed to maximize visa approval success
      </p>
    </div>

    <div className="relative">
      {/* Vertical Line */}
      <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-500 to-orange-500 rounded-full hidden lg:block"></div>

      <div className="space-y-10 lg:space-y-16">
        {[
          {
            step: "1",
            title: "Initial Consultation",
            description: "Detailed discussion of your background, goals, and visa requirements",
            icon: <Users className="w-6 h-6 lg:w-8 lg:h-8" />,
            details: "We evaluate your profile and suggest the most suitable visa strategy."
          },
          {
            step: "2",
            title: "Document Preparation",
            description: "End-to-end support in compiling accurate and complete documents",
            icon: <FileText className="w-6 h-6 lg:w-8 lg:h-8" />,
            details: "Includes SOP guidance, financial documents, and compliance checks."
          },
          {
            step: "3",
            title: "Application Submission",
            description: "Error-free visa filing with embassy-aligned documentation",
            icon: <CheckCircle className="w-6 h-6 lg:w-8 lg:h-8" />,
            details: "We ensure correct submission, timelines, and form accuracy."
          },
          {
            step: "4",
            title: "Interview & Decision",
            description: "Mock interviews and final-stage preparation",
            icon: <Award className="w-6 h-6 lg:w-8 lg:h-8" />,
            details: "Confidence building, interview tips, and post-submission follow-ups."
          }
        ].map((item, index) => (
          <div
            key={index}
            className={`flex flex-col lg:flex-row items-start lg:items-center ${
              index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
            }`}
          >
            <div className="w-full lg:flex-1 lg:px-8">
              <div
                className={`rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all bg-gradient-to-br ${
                  index % 2 === 0 ? "from-blue-50 to-white" : "from-orange-50 to-white"
                }`}
              >
                <div className="flex gap-4">
                  <div
                    className={`w-14 h-14 lg:w-16 lg:h-16 rounded-xl flex items-center justify-center text-white ${
                      index % 2 === 0
                        ? "bg-gradient-to-br from-blue-500 to-blue-600"
                        : "bg-gradient-to-br from-orange-500 to-orange-600"
                    }`}
                  >
                    {item.icon}
                  </div>

                  <div>
                    <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 mb-2">
                      {item.description}
                    </p>
                    <p className="text-sm text-gray-500">
                      {item.details}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Center Dot */}
            <div className="hidden lg:block w-8 h-8 bg-white border-4 border-blue-500 rounded-full relative z-10"></div>

            <div className="hidden lg:block lg:flex-1"></div>
          </div>
        ))}
      </div>
    </div>

    

  </div>
</section>


      {/* Success Stories Section */}
      <section className="bg-white py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-4xl font-bold mb-16 text-center">
            Success Stories
          </div>
          <GoogleReviews />
        </div>
      </section>
      {/* <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Success Stories</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real experiences from our clients who achieved their visa goals with our expert guidance
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="flex items-center mb-6">
                  <img
                    src={story.image}
                    alt={story.name}
                    className="w-16 h-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{story.name}</h4>
                    <p className="text-sm text-gray-600">{story.visa} • {story.country}</p>
                  </div>
                </div>
                
                <div className="flex mb-4">
                  {[...Array(story.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 italic leading-relaxed">
                  "{story.story}"
                </blockquote>
              </div>
            ))}
          </div>
        </div>
      </section> */}
      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Get answers to the most common questions about visa applications
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300">
                <h4 className="font-bold text-gray-900 mb-3 text-lg">{faq.question}</h4>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <div id="contact-form">
  <ContactUsForm />
</div>



   

    

    
      
    </div>
  );
};

export default VisaPage;  